/**
 * System role templates. Cloned into each org's `roles` table at provisioning
 * (is_system=true), so every role row is org-owned and uses the identical
 * strict RLS predicate. Grants are stored literally (wildcards kept as-is) and
 * expanded at check time, so future permission keys are auto-included.
 */

export type ScopeLevel = 'org' | 'site' | 'team' | 'group'

export interface RoleTemplate {
  /** Stable template key, also used as the per-org role key. */
  key: string
  name: string
  scopeLevel: ScopeLevel
  description: string
  grants: string[]
}

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    key: 'org_owner',
    name: 'Org Owner',
    scopeLevel: 'org',
    description:
      'Full control of the org including ownership transfer and deletion. At least one Org Owner must always exist.',
    grants: [
      '*',
      'org.transfer_ownership',
      'org.delete',
      'settings.manage',
      'billing.manage',
      'roles.manage',
      'members.manage',
      'audit.read',
      'support.submit',
    ],
  },
  {
    key: 'org_admin',
    name: 'Org Admin',
    scopeLevel: 'org',
    description:
      'Day-to-day org administrator. Manages everything operational across all sites/teams/groups, but cannot transfer ownership, delete the org, or manage billing.',
    grants: [
      'settings.read',
      'settings.manage',
      'roles.read',
      'roles.manage',
      'members.read',
      'members.manage',
      'people.*',
      'groups.*',
      'teams.*',
      'sites.*',
      'events.*',
      'calendar.*',
      'resources.*',
      'content.*',
      'media.read',
      'media.upload',
      'media.delete',
      'forms.*',
      'workflows.*',
      'lists.*',
      'checkins.*',
      'services.*',
      'songs.*',
      'ai.use',
      'audit.read',
      'notifications.read',
      'reports.read',
      'reports.manage',
      'support.submit',
    ],
  },
  {
    key: 'org_staff',
    name: 'Org Staff',
    scopeLevel: 'org',
    description:
      'General staff member with broad read and operational write across ministries, but no destructive/structural admin.',
    grants: [
      'people.read',
      'people.update',
      'people.create',
      'people.notes.read',
      'groups.read',
      'groups.update',
      'teams.read',
      'sites.read',
      'events.read',
      'events.update',
      'events.create',
      'calendar.read',
      'resources.read',
      'resources.book',
      'content.read',
      'forms.read',
      'forms.submit',
      'forms.responses.read',
      'lists.read',
      'media.read',
      'media.upload',
      'checkins.read',
      'checkins.operate',
      'services.read',
      'services.create',
      'services.update',
      'songs.read',
      'songs.manage',
      'ai.use',
      'notifications.read',
      'reports.read',
    ],
  },
  {
    key: 'org_billing',
    name: 'Org Billing',
    scopeLevel: 'org',
    description: 'Dedicated finance/billing contact. Sees billing and financial reporting only.',
    grants: [
      'billing.read',
      'billing.manage',
      'finance.read',
      'finance.manage',
      'finance.payments.read',
      'finance.payments.refund',
      'finance.reports.read',
      'settings.read',
      'notifications.read',
    ],
  },
  {
    key: 'org_auditor',
    name: 'Org Auditor',
    scopeLevel: 'org',
    description:
      'Read-only oversight. Views configuration, members, and the audit log across the org but changes nothing.',
    grants: [
      'settings.read',
      'roles.read',
      'members.read',
      'people.read',
      'groups.read',
      'teams.read',
      'sites.read',
      'events.read',
      'finance.read',
      'finance.reports.read',
      'content.read',
      'forms.responses.read',
      'lists.read',
      'media.read',
      'services.read',
      'songs.read',
      'audit.read',
      'notifications.read',
      'reports.read',
      'support.view',
    ],
  },
  {
    key: 'site_director',
    name: 'Site Director',
    scopeLevel: 'site',
    description:
      'Campus pastor/director. Full operational control of one site and everything tagged to it.',
    grants: [
      'sites.read',
      'sites.update',
      'teams.*',
      'groups.*',
      'people.read',
      'people.update',
      'people.create',
      'people.notes.read',
      'people.notes.write',
      'events.*',
      'calendar.read',
      'calendar.manage',
      'resources.read',
      'resources.book',
      'resources.manage',
      'checkins.read',
      'checkins.operate',
      'checkins.manage',
      'forms.*',
      'lists.read',
      'workflows.read',
      'media.read',
      'media.upload',
      'media.delete',
      'services.*',
      'songs.read',
      'songs.manage',
      'ai.use',
      'members.read',
      'notifications.read',
      'reports.read',
      'reports.manage',
    ],
  },
  {
    key: 'site_scheduler',
    name: 'Site Scheduler',
    scopeLevel: 'site',
    description: 'Manages calendar, resource bookings, and event logistics for a site.',
    grants: [
      'sites.read',
      'calendar.read',
      'calendar.manage',
      'resources.read',
      'resources.book',
      'resources.manage',
      'events.read',
      'events.update',
      'events.create',
      'teams.read',
      'groups.read',
      'services.read',
      'services.create',
      'services.update',
      'songs.read',
      'notifications.read',
    ],
  },
  {
    key: 'team_leader',
    name: 'Team Leader',
    scopeLevel: 'team',
    description:
      'Ministry team leader. Manages the team, its members, and all groups nested under it (inherits down to child groups).',
    grants: [
      'teams.read',
      'teams.update',
      'teams.members.manage',
      'groups.*',
      'people.read',
      'people.update',
      'people.notes.read',
      'people.notes.write',
      'events.*',
      'checkins.read',
      'checkins.operate',
      'forms.read',
      'forms.submit',
      'forms.responses.read',
      'lists.read',
      'media.read',
      'media.upload',
      'resources.read',
      'resources.book',
      'services.read',
      'services.create',
      'services.update',
      'songs.read',
      'songs.manage',
      'ai.use',
      'notifications.read',
      'reports.read',
    ],
  },
  {
    key: 'team_member',
    name: 'Team Member',
    scopeLevel: 'team',
    description:
      'Contributing member of a ministry team. Collaborates within the team and its groups.',
    grants: [
      'teams.read',
      'groups.read',
      'groups.update',
      'people.read',
      'events.read',
      'events.update',
      'events.create',
      'checkins.read',
      'checkins.operate',
      'forms.read',
      'forms.submit',
      'resources.read',
      'resources.book',
      'services.read',
      'songs.read',
      'notifications.read',
    ],
  },
  {
    key: 'group_leader',
    name: 'Group Leader',
    scopeLevel: 'group',
    description:
      'Public/small-group leader. Manages one group, its membership, events and signups, and can contact its people.',
    grants: [
      'groups.read',
      'groups.update',
      'groups.members.manage',
      'people.read',
      'people.contact',
      'events.read',
      'events.update',
      'events.create',
      'events.signups.read',
      'events.signups.manage',
      'checkins.read',
      'checkins.operate',
      'forms.read',
      'forms.submit',
      'forms.responses.read',
      'media.read',
      'media.upload',
      'notifications.read',
    ],
  },
  {
    key: 'group_member',
    name: 'Group Member',
    scopeLevel: 'group',
    description: 'Ordinary member of a group. Sees the roster and events and can RSVP/sign up.',
    grants: [
      'groups.read',
      'people.read',
      'events.read',
      'events.signups.create',
      'checkins.self',
      'forms.read',
      'forms.submit',
      'notifications.read',
    ],
  },
]

export const ROLE_TEMPLATE_BY_KEY: Record<string, RoleTemplate> = Object.fromEntries(
  ROLE_TEMPLATES.map((r) => [r.key, r]),
)
